import React, { useState, useEffect } from 'react';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    // Update page title and meta tags
    document.title = 'FAQ | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Frequently asked questions about our web design services, pricing, timelines, and ownership policies. Get answers now.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'website design FAQ, web design questions, website cost, hosting questions, SEO FAQ, BC web design help, Canada website answers');
    }
  }, []);

  const faqs = [
    {
      question: 'How long does it take to build a website?',
      answer: 'Most websites are completed within 7-14 days, depending on the package and complexity. Our Starter websites typically take 5-7 days, Business websites 7-10 days, and Premium websites 10-14 days. Rush delivery options are available for an additional fee.'
    },
    {
      question: 'What is included in the website design packages?',
      answer: 'All packages include mobile-responsive design, contact forms, social media integration, basic SEO, and hosting setup. Higher-tier packages include additional pages, advanced SEO, Google Maps integration, galleries, and blog functionality. Check our Pricing page for detailed comparisons.'
    },
    {
      question: 'Do you offer website maintenance and updates?',
      answer: 'Yes! We offer three maintenance plans: Basic ($100/month) includes hosting and security updates, Standard ($200/month) adds 1 hour of monthly updates, and Premium ($300/month) includes 3 hours of updates plus monthly blog posts. All plans include hosting, SSL certificates, and daily backups.'
    },
    {
      question: 'Can I update my website content myself?',
      answer: 'Absolutely! We can add a client dashboard (+$500) that allows you to update products, images, and content yourself. We also provide training and documentation. Alternatively, our maintenance plans include monthly update hours if you prefer us to handle changes.'
    },
    {
      question: 'What is SEO, AEO, and GEO optimization?',
      answer: 'SEO (Search Engine Optimization) helps your site rank on Google. AEO (Answer Engine Optimization) optimizes for AI assistants like ChatGPT and Siri. GEO (Generative Engine Optimization) ensures your business appears in AI-generated search results. Our $500/month package covers all three for maximum online visibility.'
    },
    {
      question: 'Do you provide e-commerce functionality?',
      answer: 'Yes! Our e-commerce package (+$799 ) includes a full online store with 30 products, payment integration (Stripe/PayPal), shopping cart, checkout system, and inventory management. Additional products are $100 per 15 items. We can also integrate with existing platforms like Shopify.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, bank transfers, and e-transfers for Canadian clients. Payment terms vary by package: 50% deposit to start, 50% upon completion. Monthly maintenance plans are billed automatically on the 1st of each month.'
    },
    {
      question: 'Will my website work on mobile devices?',
      answer: 'Yes, all our websites are fully responsive and optimized for mobile devices, tablets, and desktops. Mobile-first design is standard across all packages, ensuring your site looks perfect on any screen size. We test on multiple devices before launch.'
    },
    {
      question: 'Do you provide domain and hosting services?',
      answer: 'Yes! Domain registration and management is available for $25/month. All maintenance plans include hosting, SSL certificates, daily backups, and 99.9% uptime guarantee. You can also use your existing domain and hosting if preferred.'
    },
    {
      question: 'What if I need changes after the website is launched?',
      answer: 'All packages include 30-90 days of post-launch support (varies by package). After that, you can purchase our maintenance plans or pay hourly rates for updates. Minor tweaks are often included free within the support period. We\'re always here to help your business grow.'
    },
    {
      question: 'Can you help with content and images?',
      answer: 'Yes! We can help write website copy and source professional stock images. If you have specific content or photos, we\'ll integrate them. Our Premium package includes professional copywriting. Product photography services are available separately for e-commerce clients.'
    },
    {
      question: 'Do you serve clients outside Canada and the USA?',
      answer: 'Yes! We serve clients worldwide. Our website design services are available to businesses anywhere in the world. We work remotely and communicate via email, video calls, and project management tools. Time zone differences are easily managed, and we accept various international payment methods. Contact us to discuss your project regardless of your location.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Schema.org FAQPage structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-b from-[#F8F9FA] to-white overflow-hidden">
        {/* No Upfront Payment Ribbon */}
        <div className="absolute top-20 right-8 z-20 animate-pulse">
          <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-8 py-4 rounded-xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-300">
            <div className="flex items-center gap-3">
              <i className="ri-gift-line text-3xl"></i>
              <div>
                <p className="text-sm font-bold uppercase tracking-wider">No Upfront Payment</p>
                <p className="text-xs opacity-90">Try Before You Pay</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block bg-blue-100 text-[#0d3b66] text-sm font-semibold px-4 py-2 rounded-full mb-6">
            HELP CENTER
          </span>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about our website design services
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-[#0d3b66] transition-all duration-300"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-blue-50 transition-colors"
                >
                  <h3 className="text-xl font-semibold text-gray-900 pr-4" itemProp="name">{faq.question}</h3>
                  <div className={`w-8 h-8 bg-[#0d3b66] rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-white text-2xl"></i>
                  </div>
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div className="px-8 pb-6">
                    <p className="text-base text-gray-600 leading-relaxed" itemProp="text">{faq.answer}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-question-line text-3xl text-[#0d3b66]"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Can't find the answer you're looking for? Our team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="cursor-pointer">
                <button className="bg-[#0d3b66] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0a2d4d] transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap">
                  Contact Us
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Explore More</h2>
            <p className="text-lg text-gray-600">Learn more about our services and pricing</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <a href="/services" className="bg-[#F8F9FA] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-service-line text-2xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Our Services</h3>
              <p className="text-base text-gray-600">Explore all our website design packages and add-ons</p>
            </a>
            
            <a href="/pricing" className="bg-[#F8F9FA] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-price-tag-3-line text-2xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pricing</h3>
              <p className="text-base text-gray-600">View detailed pricing and compare packages</p>
            </a>
            
            <a href="/portfolio" className="bg-[#F8F9FA] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-gallery-line text-2xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Portfolio</h3>
              <p className="text-base text-gray-600">See examples of our recent work</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
